/**
 * resolveClientFeatures.ts
 *
 * Single source of truth for determining which portal features
 * a client has access to. Merges two sources:
 *
 *  1. `contract_addons`  — add-ons activated per contract (new flow)
 *  2. `client_addons`    — legacy/direct add-ons assigned to client
 *
 * RULES:
 *  - A feature is active if EITHER source has status = "active".
 *  - `dvd_online` and `media_packages` both map to `dvd_online` feature key.
 *  - Falls back to `tenant_modules` only when neither table has rows for the
 *    client (backward-compat for pre-addon tenants).
 */

import { supabase } from "@/lib/supabaseClient";

/* ─── Feature map returned to callers ──────────────────────── */

export interface ClientFeatureMap {
  /** Invitación Digital */
  digital_invitation: boolean;
  /** Libro de Firmas */
  signature_book: boolean;
  /** Selección de Fotos */
  photo_selection: boolean;
  /** Entrega Final */
  final_deliveries: boolean;
  /** Video Online / DVD (includes media_packages) */
  dvd_online: boolean;
  /** Citas y Reuniones */
  appointments: boolean;
  /** Asistente IA (informational — no portal section) */
  ai_sales_assistant: boolean;
}

/* ─── Internal row types ─────────────────────────────────────  */
interface AddonRow {
  addon_key: string;
  status: string;
}

/* ─── Main resolver ──────────────────────────────────────────  */

/**
 * Resolves which features are active for a client by combining
 * `contract_addons` and `client_addons` tables.
 *
 * @param tenantId  - The tenant the client belongs to
 * @param clientId  - The client whose features are being resolved
 * @returns         A `ClientFeatureMap` with boolean flags per feature
 */
export async function resolveClientFeatures(
  tenantId: string,
  clientId: string,
): Promise<ClientFeatureMap> {
  // Fetch both sources in parallel
  const [contractAddonsRes, clientAddonsRes] = await Promise.all([
    supabase
      .from("contract_addons")
      .select("addon_key, status")
      .eq("tenant_id", tenantId)
      .eq("client_id", clientId),
    supabase
      .from("client_addons")
      .select("addon_key, status")
      .eq("tenant_id", tenantId)
      .eq("client_id", clientId),
  ]);

  if (contractAddonsRes.error) {
    console.error("[resolveClientFeatures] contract_addons:", contractAddonsRes.error.message);
  }
  if (clientAddonsRes.error) {
    console.error("[resolveClientFeatures] client_addons:", clientAddonsRes.error.message);
  }

  const contractRows = (contractAddonsRes.data ?? []) as AddonRow[];
  const clientRows   = (clientAddonsRes.data   ?? []) as AddonRow[];

  const allRows = [...contractRows, ...clientRows];

  // Backward-compat: no addon rows at all → fall back to tenant_modules
  if (allRows.length === 0) {
    return await getFallbackFeatures(tenantId);
  }

  /**
   * A feature is active if at least one row across both tables
   * has the matching addon_key AND status = "active".
   */
  const isActive = (key: string): boolean =>
    allRows.some((r) => r.addon_key === key && r.status === "active");

  return {
    digital_invitation: isActive("digital_invitation"),
    signature_book:     isActive("signature_book"),
    photo_selection:    isActive("photo_selection"),
    final_deliveries:   isActive("final_deliveries"),
    // dvd_online covers both dvd_online + media_packages keys
    dvd_online:         isActive("dvd_online") || isActive("media_packages"),
    appointments:       isActive("appointments"),
    ai_sales_assistant: isActive("ai_sales_assistant"),
  };
}

/* ─── Backward-compat fallback (legacy tenant_modules) ──────── */

async function getFallbackFeatures(tenantId: string): Promise<ClientFeatureMap> {
  const { data: mods } = await supabase
    .from("tenant_modules")
    .select("module_id, is_enabled")
    .eq("tenant_id", tenantId);

  const { data: saasModules } = await supabase
    .from("saas_modules")
    .select("id, module_key");

  const keyById: Record<string, string> = {};
  (saasModules ?? []).forEach((m: { id: string; module_key: string }) => {
    keyById[m.id] = m.module_key;
  });

  const enabledKeys = new Set(
    (mods ?? [])
      .filter((m: { is_enabled: boolean }) => m.is_enabled)
      .map((m: { module_id: string }) => keyById[m.module_id])
      .filter(Boolean),
  );

  return {
    digital_invitation: enabledKeys.has("digital_invitations"),
    signature_book:     false, // not in legacy modules
    photo_selection:    enabledKeys.has("photo_selection"),
    final_deliveries:   enabledKeys.has("final_deliveries"),
    dvd_online:         enabledKeys.has("media_packages"),
    appointments:       enabledKeys.has("appointments"),
    ai_sales_assistant: false,
  };
}

/* ─── Spot-check helper ──────────────────────────────────────── */

/**
 * Quickly checks if a client has access to a single feature.
 * Checks both `contract_addons` and `client_addons`.
 *
 * @param tenantId  - Tenant ID
 * @param clientId  - Client ID
 * @param addonKey  - Feature key to check (e.g. "photo_selection")
 * @returns         true if the feature is active in either source
 */
export async function clientHasFeature(
  tenantId: string,
  clientId: string,
  addonKey: string,
): Promise<boolean> {
  // dvd_online also covers media_packages
  const keysToCheck =
    addonKey === "dvd_online"
      ? ["dvd_online", "media_packages"]
      : [addonKey];

  const [contractRes, clientRes] = await Promise.all([
    supabase
      .from("contract_addons")
      .select("id")
      .eq("tenant_id", tenantId)
      .eq("client_id", clientId)
      .in("addon_key", keysToCheck)
      .eq("status", "active")
      .maybeSingle(),
    supabase
      .from("client_addons")
      .select("id")
      .eq("tenant_id", tenantId)
      .eq("client_id", clientId)
      .in("addon_key", keysToCheck)
      .eq("status", "active")
      .maybeSingle(),
  ]);

  return !!contractRes.data || !!clientRes.data;
}